import React, { useState, useEffect, useRef, useReducer, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams, Link } from 'cccisd-react-router';
import notification from 'cccisd-notification';
import axios from 'cccisd-axios';
import Modal from 'cccisd-modal';

import Loader from 'cccisd-loader';
import { useSelector } from 'react-redux';
import { ResourceCenterWithCollection } from 'cccisd-laravel-resources';
import AssessmentCards from './AssessmentCards';
import Filters from './Filters';
import { filterSessionSettings } from 'js/helpers';
import useStudentPicker from 'js/hooks/useStudentPicker.js';
import progressQuery from './progress.graphql';
import style from './style.css';
// Icons
import ShareLogo from 'images/SHARE-Logo.svg';
import IconPlus from 'cccisd-icons/plus-circle2';
import IconInfo from 'cccisd-icons/info2';

const Fortress = window.cccisd.fortress;
const creatingPawnId = Fortress.user.acting.id;

const AdministerPage = props => {
    const [studentPickerModalOpen, setStudentPickerModalOpen] = useState(false);
    const [forceReloadQueries, setForceReloadQueries] = useReducer(s => s + 1, 0);
    const [selectedStudentsList, setSelectedStudentsList] = useState([]);
    const [assessmentList, setAssessmentList] = useState([]);
    const [filters, setFilters] = useState({
        ready: false,
        stopped: false,
        completed: false,
        assessments: new Set(),
        archived: 'hide',
    });
    const creationModalRef = useRef(null);
    const studentPickerModalRef = useRef(null);
    const assessmentPlanList = useSelector(state => state.app.share.assessmentPlans);
    const { pawnId } = useParams();

    const {
        loading: studentPickerLoading,
        total: totalStudents,
        selected: selectedStudents,
        renderField: renderStudentPicker,
    } = useStudentPicker({
        initialValue: Number(pawnId),
        isMultiple: true,
    });

    const studentPickerSecondary = useStudentPicker({
        initialValue: [],
        isMultiple: false,
    });

    useEffect(() => {
        if (selectedStudents && !studentPickerModalOpen) {
            if (!Array.isArray(selectedStudents)) {
                setSelectedStudentsList([selectedStudents]);
            } else {
                setSelectedStudentsList(selectedStudents);
            }
        }
    }, [selectedStudents, studentPickerModalOpen]);

    useEffect(() => {
        if (studentPickerModalOpen && studentPickerSecondary.selected) {
            setSelectedStudentsList([studentPickerSecondary.selected]);
        }
    }, [studentPickerSecondary.selected, studentPickerModalOpen]);

    useEffect(() => {
        const studentSync = async () => {
            try {
                const response = await axios.get('/api/studentSync');
                if (response?.data?.status === 'success' && response?.data?.data.refresh === true) {
                    window.location.reload();
                }
            } catch (error) {
                console.error('StudentSync API Error:', error);
            }
        };

        studentSync();
    }, []);

    const getAssessmentList = () => {
        if (assessmentPlanList && assessmentPlanList.length > 0) {
            const assessments = new Map();
            assessmentPlanList.forEach(plan => {
                plan.sessionList.forEach(session => {
                    session.settings.sessionCollection.forEach(survey => {
                        const publishedMetadata = JSON.parse(survey.resource.published_metadata);
                        const info = {
                            id: survey.id,
                            label: publishedMetadata.surveyInfo.surveyLabel,
                            description: survey.resource.published_description || '',
                            handle: publishedMetadata.surveyInfo.surveyHandle,
                        };
                        assessments.set(info.id, info);
                    });
                });
            });
            setAssessmentList(Array.from(assessments.values()));
            return assessments;
        }
    };

    useMemo(() => {
        getAssessmentList();
    }, [assessmentPlanList]);

    const onSubmit = async vals => {
        const deployments = new Map();
        vals.sessions.forEach(session => {
            deployments.set(session.settings.reporter, session.deploymentId);
        });
        const respondentIds = vals.respondents.map(r => r.id);
        let sessionDeploymentInfo = {};

        await axios.post(window.cccisd.boilerplate.route('api.assignment.plan.create'), {
            creatingPawnId,
            label: vals.planName,
            description: vals.planDescription,
            pawnIds: respondentIds,
            settings: { planSettings: vals.planSettings, ...vals.custom },
            sessions: vals.sessions.map((session, i) => {
                return {
                    label: session.label,
                    deploymentId: session.deploymentId,
                    sessionType: session.sessionType,
                    offset: session.offset,
                    opensAt: session.opensAt,
                    closesAt: session.closesAt,
                    schedule: session.schedule,
                    settings: filterSessionSettings(session.settings),
                    sessionDeploymentInfo: session.settings.sessionId === 'Student0' ? sessionDeploymentInfo : null,
                };
            }),
        });
        setForceReloadQueries();
        notification({
            message: 'Assessments assigned successfully.',
        });
        if (studentPickerModalRef.current) {
            studentPickerModalRef.current.close();
        }
        creationModalRef.current.close();
    };

    const renderResourceTiles = (data, renderDefault) => {
        return <div className={style.resourceTiles}>{renderDefault(data)}</div>;
    };

    const handleAssignAssessments = () => {
        if (selectedStudentsList.length !== 1) {
            if (studentPickerModalRef.current) {
                studentPickerModalRef.current.open();
                setStudentPickerModalOpen(true);
            }
            return;
        }
        if (creationModalRef.current) {
            creationModalRef.current.open();
        }
    };

    const handleRemoveFilter = filterKey => {
        setFilters(prevFilters => {
            const newFilters = { ...prevFilters };
            if (filterKey === 'assessments') {
                newFilters[filterKey] = new Set();
            } else if (filterKey === 'archived') {
                newFilters[filterKey] = 'hide';
            } else {
                newFilters[filterKey] = false;
            }
            return newFilters;
        });
    };

    const handleRemoveAllFilters = () => {
        setFilters({
            ready: false,
            stopped: false,
            completed: false,
            assessments: new Set(),
            archived: 'hide',
        });
    };

    const thereAreFiltersSet =
        filters.ready ||
        filters.stopped ||
        filters.completed ||
        filters.assessments.size > 0 ||
        filters.archived === 'show';

    const renderFilterChips = () => {
        const sKeys = {
            ready: 'Ready to Complete',
            stopped: 'Stopped',
            completed: 'Completed',
            assessments: 'Assessments',
            archived: 'Archived',
        };

        return (
            <div className="d-flex gap-2 mt-2">
                {Object.keys(filters).map(key => {
                    if (!filters[key] || filters[key] === 'hide') return null;
                    if (key === 'assessments' && filters[key].size === 0) return null;
                    return (
                        <span className="badge bg-primary d-flex align-items-center" key={key}>
                            {sKeys[key]} {key === 'assessments' ? `(${filters[key].size})` : ''}
                            <button
                                type="button"
                                className="btn-close btn-close-white ms-2"
                                aria-label="Remove filter"
                                onClick={() => handleRemoveFilter(key)}
                            />
                        </span>
                    );
                })}
                {thereAreFiltersSet && (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        aria-label="Remove all filters"
                        onClick={() => handleRemoveAllFilters()}
                    >
                        Clear All
                    </button>
                )}
            </div>
        );
    };

    if (studentPickerLoading) {
        return <Loader loading />;
    }

    if (totalStudents < 1) {
        return (
            <div className={'py-4 ' + style.administerPage}>
                <div className="alert alert-primary text-center fs-3" role="alert">
                    <IconInfo /> Add Students on the{' '}
                    <Link
                        to={{
                            pathname: '/students',
                        }}
                        className="alert-link"
                    >
                        Students
                    </Link>{' '}
                    page to administer assessments.
                </div>
            </div>
        );
    }

    return (
        <div className={`container py-4 ${style.administerPage}`}>
            <div className={style.filterBar}>
                <div className={style.studentSelection} data-student-area>
                    {renderStudentPicker()}
                    <div className={style.selectedStudentInfo}>
                        {selectedStudentsList.length === 1 && (
                            <div className="d-flex mt-2" style={{ fontSize: '0.8rem' }}>
                                <div>
                                    <strong>School:</strong> {selectedStudentsList[0].school}
                                </div>
                                {selectedStudentsList[0].studentId && (
                                    <div className="ms-4">
                                        <strong>Student ID:</strong> {selectedStudentsList[0].studentId}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="text-end">
                    <div>
                        <Filters assessmentList={assessmentList} setFilters={setFilters} filters={filters} />
                    </div>
                    {renderFilterChips()}
                </div>
            </div>
            {!studentPickerLoading && (
                <div>
                    <Modal
                        trigger={null}
                        ref={studentPickerModalRef}
                        beforeClose={() => setStudentPickerModalOpen(false)}
                        title="Select a Student to Assign Assessments"
                        render={() => {
                            return (
                                <div>
                                    <div style={{ marginBottom: '1em' }}>{studentPickerSecondary.renderField()}</div>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            creationModalRef.current.open();
                                        }}
                                        disabled={selectedStudentsList.length !== 1}
                                        data-assign-assessments-secondary
                                    >
                                        <IconPlus /> Assign Assessments{' '}
                                        {selectedStudentsList.length === 1
                                            ? `for ${selectedStudentsList[0].name}`
                                            : '...'}
                                    </button>
                                </div>
                            );
                        }}
                    />

                    <Modal
                        trigger={null}
                        size="99vw"
                        ref={creationModalRef}
                        title={`Assign Assessments ${
                            selectedStudentsList.length === 1 ? `for ${selectedStudentsList[0].name}` : ''
                        }`}
                        beforeClose={() => {
                            props.history.push(`${props.location.pathname}`);
                        }}
                    >
                        <ResourceCenterWithCollection
                            collectionUserList={selectedStudentsList.map(s => ({ id: s.pawnId, name: s.name }))}
                            handle="default"
                            onSubmit={onSubmit}
                            collectionName="Assessment Plan"
                            logo={ShareLogo}
                            renderResourceTiles={renderResourceTiles}
                            collectionBuckets={['Student', 'Caregiver', 'Educator', 'Mental Health Provider']}
                            progressQuery={progressQuery}
                            addToCollectionButtonLabel="Add"
                            preventLayoutChange
                        />
                    </Modal>
                    <AssessmentCards
                        selectedStudents={selectedStudentsList}
                        forceReloadQueries={forceReloadQueries}
                        setForceReloadQueries={setForceReloadQueries}
                        filters={filters}
                        thereAreFiltersSet={thereAreFiltersSet}
                        handleAssignAssessments={handleAssignAssessments}
                    />
                </div>
            )}
        </div>
    );
};

export default AdministerPage;

AdministerPage.propTypes = {
    progressQuery: PropTypes.object,
    collectionBuckets: PropTypes.array,
    contactTypes: PropTypes.array,
    logo: PropTypes.string,
    addToCollectionButtonLabel: PropTypes.string,
    history: PropTypes.object,
    location: PropTypes.object,
};

AdministerPage.defaultProps = {
    progressQuery: null,
    collectionBuckets: ['Student', 'Caregiver', 'Educator', 'Mental Health Provider'],
    contactTypes: [],
    logo: '',
    addToCollectionButtonLabel: 'Add',
};
